import React, {useState} from "react";
import {Membership} from "../../models/membership";
import {useTranslation} from "react-i18next";
import {RoleIcon} from "../../utils/RoleIcon";
import {UserRole} from "../../models/account";
import OptionDialog from "../dialogs/OptionDialog";
import {deleteTourMembership, updateTourMembership} from "../../api/membershipTourApi";
import ConfirmationDialog from "../dialogs/ConfirmationDialog";
import {useNavigate} from "react-router-dom";
import {NAV} from "../../constants/navigation";
import {QUERY_PARAMS} from "../../constants/queryParams";

interface MemberProps {
    ownRole: UserRole;
    membership: Membership;
    tourId: number;
    handleReload: () => void;
    validFrom?: string | null;
    validTo?: string | null;
}

export default function Member({
                                   membership,
                                   ownRole,
                                   tourId,
                                   handleReload,
                                   validFrom,
                                   validTo
                               }: MemberProps) {

    const {t} = useTranslation();
    const navigate = useNavigate();

    const [showDecisionDialog, setShowDecisionDialog] = useState(false);
    const [menuOpen, setMenuOpen] = useState(false);
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const [showConfirmAdminDialog, setShowConfirmAdminDialog] = useState(false);
    const [dropdownPos, setDropdownPos] = useState<{ top: number; left: number } | null>(null);

    // const toggleMenu = () => setMenuOpen(prev => !prev);

    const toggleMenu = (e: React.MouseEvent) => {
        setMenuOpen(prev => !prev);

        const button = e.currentTarget as HTMLElement;
        const rect = button.getBoundingClientRect();

        const dropdownHeight = 140; // geschätzt, kann angepasst werden
        const spaceBelow = window.innerHeight - rect.bottom;
        const openUpwards = spaceBelow < dropdownHeight;

        const top = openUpwards
            ? rect.top - dropdownHeight - 4
            : rect.bottom + 4;

        setDropdownPos({
            top,
            left: rect.left
        });
    };

    const isAdminOrCreator = (role: UserRole) =>
        role == UserRole.ADMIN || role == UserRole.CREATOR;

    const isWithinTimeWindow = () => {
        if (!validFrom || !validTo) return false;

        const now = new Date();
        const from = new Date(validFrom);
        const to = new Date(validTo);

        return now >= from && now <= to;
    };

    const showRoleIcon = isAdminOrCreator(membership.userRole);

    const showAnswerRequestButton =
        isAdminOrCreator(ownRole) &&
        membership.userRole === UserRole.APPLIED;

    const showViewButton =
        membership.userRole !== UserRole.APPLIED &&
        isWithinTimeWindow();

    const showMenu =
        membership.userRole !== UserRole.APPLIED &&
        ((ownRole === UserRole.ADMIN && membership.userRole === UserRole.MEMBER) ||
            (ownRole === UserRole.CREATOR && membership.userRole !== UserRole.CREATOR));

    const canGrantAdmin =
        isAdminOrCreator(ownRole) && membership.userRole === UserRole.MEMBER;

    async function handleUpdateMember(role: UserRole) {
        const dto = {role};
        await updateTourMembership(membership.accountId, tourId, dto);
        handleReload();
        closeAllDialogs();
    }

    async function handleDeleteMember() {
        await deleteTourMembership(membership.accountId, tourId);
        handleReload();
        closeAllDialogs();
    }

    function closeAllDialogs() {
        setMenuOpen(false);
        setShowConfirmAdminDialog(false);
        setShowDeleteDialog(false);
        setShowDecisionDialog(false);
    }

    return (
        <div className={`member-row ${membership.userRole === UserRole.APPLIED ? "applied" : ""}`}>

            {/* ICON */}
            <div className="member-icon">
                {showRoleIcon ? (
                    <RoleIcon role={membership.userRole}/>
                ) : (
                    <div style={{width: "20px"}}></div>
                )}
            </div>

            {/* NAME */}
            <div className="member-name">
                {membership.lastName}, {membership.firstName}
            </div>

            {/* ACTIONS */}
            <div className="member-actions">

                {/* BUTTON-SPALTE */}
                <div className="member-actions">
                    {showAnswerRequestButton && (
                        <button className="form-button-secondary" onClick={() => setShowDecisionDialog(true)}>
                            {t("tours.buttons.answerRequest")}
                        </button>
                    )}

                    {showViewButton && (
                        <button
                            onClick={() => navigate(`/${NAV.SHOW_PERSON}?${QUERY_PARAMS.ACCOUNT}=${membership.accountCode}`)}
                            className="form-button"
                        >
                            {t("tours.buttons.showMember")}
                        </button>
                    )}
                </div>

                {/* MENÜ-SPALTE */}
                <div className="member-menu">
                    {showMenu ? (
                        <>
                            <button className="menu-button" onClick={toggleMenu}>⋮</button>

                            {menuOpen && dropdownPos && (
                                <div
                                    className="menu-dropdown"
                                    style={{
                                        top: dropdownPos.top,
                                        left: dropdownPos.left
                                    }}
                                >
                                    <button className="menu-item" onClick={() => {
                                        setShowDeleteDialog(true);
                                        setMenuOpen(false);
                                    }
                                    }>
                                        {t("tours.buttons.remove")}
                                    </button>

                                    {canGrantAdmin && (
                                        <>
                                            <div className="menu-separator"></div>
                                            <button className="menu-item" onClick={() => {
                                                setShowConfirmAdminDialog(true);
                                                setMenuOpen(false);
                                            }
                                            }>
                                                {t("tours.buttons.grantAdmin")}
                                            </button>
                                        </>
                                    )}
                                </div>
                            )}
                        </>
                    ) : (
                        <div className="menu-placeholder"></div>
                    )}
                </div>

            </div>

            {/* DIALOGE */}
            <OptionDialog
                open={showDecisionDialog}
                title={t("tours.membership.requestTitle")}
                message={t("tours.membership.requestMessage", {
                    firstName: membership.firstName,
                    lastName: membership.lastName
                })}
                firstOptionLabel={t("tours.buttons.approve")}
                secondOptionLabel={t("tours.buttons.reject")}
                onFirstOption={() => handleUpdateMember(UserRole.MEMBER)}
                onSecondOption={handleDeleteMember}
                onClose={() => setShowDecisionDialog(false)}
            />

            <ConfirmationDialog
                open={showDeleteDialog}
                title={t("tours.dialog.removeMember")}
                message={t("tours.dialog.removeMember", {
                    firstName: membership.firstName,
                    lastName: membership.lastName
                })}
                onConfirm={handleDeleteMember}
                onClose={() => setShowDeleteDialog(false)}
            />

            <ConfirmationDialog
                open={showConfirmAdminDialog}
                title={t("tours.dialog.addAdminRights")}
                message={t("tours.dialog.addAdminRightsMessage", {
                    firstName: membership.firstName,
                    lastName: membership.lastName
                })}
                onConfirm={() => handleUpdateMember(UserRole.ADMIN)}
                onClose={() => setShowDeleteDialog(false)}
            />
        </div>
    );
}